import { Dialog, DialogBackdrop, DialogPanel, DialogProps, DialogTitle } from '@headlessui/react';
import { i18n } from '@konova/i18n';
import clsx from 'clsx';
import { noop } from 'lodash';
import {
  createElement,
  CSSProperties,
  FC,
  forwardRef,
  ForwardRefExoticComponent,
  Fragment, isValidElement,
  JSXElementConstructor,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useMemo
} from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from 'react-merge-refs';
import { useIsContentScrollable } from '../../hooks/use-is-content-scrollable';
import { useThemeIndex } from '../../hooks/use-theme-index';
import { useThemeToken } from '../../hooks/use-theme-token';
import { ThemeModalWidthTokenName, ThemeZIndexLayerName } from '../../theme';
import { Card } from '../card';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { Typography } from '../typography';
import { ModalContextProvider } from './modal-context-provider';
import classes from './modal.module.scss';
import { ModalFooter } from './modal-footer';

export type ModalProps = PropsWithChildren
  & Omit<DialogProps<'div'>, 'ref' | 'size' | 'title' | 'onClose' | 'contentRef'> & {
    /**
     * Custom Modal title
     */
    title?: ReactNode;

    /**
   * Used for the aria-label for the dialog element
   */
    label: string;

    /**
   * Custom footer content
   */
    footer?: ReactNode;

    /**
   * Close event listener
   */
    onClose?: () => void;

    /**
   * Render a backdrop
   */
    backdrop?: boolean;

    /**
   * Is modal draggable
   */
    draggable?: boolean;

    /**
   * Open/Close state
   */

    open?: boolean;

    /**
   * Custom size
   */
    size?: ThemeModalWidthTokenName;

    height?: 'auto' | 'max';

    /**
   * Z index
   * @examle 12 | var(--modal-z-index)
   */
    zIndex?: ThemeZIndexLayerName;

    relative?: boolean;

    classes?: {
      panel?: string;
      content?: string;
      wrapper?: string;
      contentWrapper?: string;
    };

    noPadding?: boolean;

    /**
   * Indicates whether the variable outline is present or not.
   */
    outline?: boolean;

    /**
   * Custom classname
   */
    className?: string;

    /**
   * Optional ReactNode that defines the background of a component.
   *
   * This can be any valid React element, including a string, JSX, or another
   * React component. It is primarily used to set the visual background of the
   * component it is applied to.
   */
    background?: ReactNode;

    /**
   * Indicates whether the component, feature, or element can be dismissed or closed by the user.
   * If set to true, the component is dismissable; if false, it remains persistent.
   *
   * Optional property.
   */
    dismissable?: boolean;

    /**
   * A reference to a HTMLDivElement, used to access or manipulate the DOM element
   * directly. This is typically managed by React's ref system and allows for
   * imperatively handling DOM interactions or integration with third-party libraries.
   *
   * Optional property, and when provided, it is expected to be a React Ref object.
   */
    contentRef?: Ref<HTMLDivElement>;

    /**
   * An optional variable that can accept different types of React components.
   * It can be used to wrap around other components or elements to provide additional behavior or styling.
   * The value can either be:
   * - A standard React functional component constructor accepting props with children, or
   * - A forwarded ref exotic component accepting props with children.
   *
   * This variable is useful when you need to dynamically determine the wrapper component
   * for rendering content and supports components with children as their primary structure.
   */
    wrapper?: JSXElementConstructor<PropsWithChildren> | ForwardRefExoticComponent<PropsWithChildren>;

    /**
   * The `rootSlot` variable represents an optional React node or React element that can be used as the root slot for rendering.
   * It allows React components or elements to be passed for rendering purposes within the context of the component utilizing it.
   */
    rootSlot?: ReactNode | ReactElement;
  };

export const Modal: FC<ModalProps> = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      title,
      open,
      backdrop = true,
      footer,
      onClose,
      children,
      draggable = false,
      size = 'default',
      relative,
      noPadding,
      classes: customClasses,
      className,
      zIndex,
      background,
      dismissable = true,
      contentRef: parentContentRef,
      label,
      wrapper,
      rootSlot,
      height,
      ...props
    },
    ref
  ) => {
    const [t] = useTranslation();
    // useLockBodyScroll(open && !relative);
    const { isScrollable, contentRef } = useIsContentScrollable(open ?? false);
    const widthToken = useThemeToken(`modal--width-${size}`);
    const zIndexTokenValue = useThemeIndex(zIndex ?? null);
    const onCloseClicked = useCallback(() => {
      onClose?.();
    }, [onClose]);

    const Wrapper: FC<PropsWithChildren> = useMemo(() => ({ children }) => {
      if (wrapper) {
        return createElement(wrapper, {}, children);
      }
      return <Fragment>{children}</Fragment>;
    }, [wrapper]);


    if (!open) {
      return null;
    }

    return (
      <ModalContextProvider>
        <Dialog
          title={label ?? undefined}
          as="div"
          aria-label={label ?? undefined}
          className={clsx(classes.root, {
            [classes.relative]: relative,
            [classes.noPadding]: noPadding,
            [classes.scrollable]: isScrollable,
            [classes.maxheight]: height === 'max',
          })}
          open={open}
          onClose={dismissable ? onCloseClicked : noop}
          style={{
            '--modal--panel-index': zIndexTokenValue,
            '--modal--backdrop-index': zIndexTokenValue
          } as CSSProperties}
        >
          {backdrop && (
            <DialogBackdrop
              className={classes.backdrop}
              style={{
                '--modal--panel-index': zIndexTokenValue,
                '--modal--backdrop-index': zIndexTokenValue
              } as CSSProperties}
            />
          )}

          {background}
          <Wrapper>
            <DialogPanel
              {...props}
              aria-label={label}
              className={clsx(
                classes.panel,
                className,
                'modal-component',
                { [classes.draggable]: draggable },
                customClasses?.panel
              )}
              style={{ '--modal--width': widthToken, zIndex } as CSSProperties}
            >
              {rootSlot}
              <Card className={clsx(classes.card)} ref={ref}>
                <Card.Header
                  className={clsx(classes.header, { [classes.close]: !!onClose })}
                  suffix={(
                    <>
                      {onClose && (
                        <IconButton
                          label={t('common.close')}
                          variant="text"
                          color="grey"
                          size="lg"
                          data-testid="close-modal"
                          aria-label={i18n.t('common.close')}
                          onClick={onCloseClicked}
                        >
                          <Icon icon={['far', 'times']} />
                        </IconButton>
                      )}
                    </>
                  )}
                >
                  {title && (
                    <DialogTitle as={Fragment}>
                      {isValidElement(title) ? (
                        title
                      ) : (
                        <Typography as="h3" variant="h3">
                          {title}
                        </Typography>
                      )}
                    </DialogTitle>
                  )}
                </Card.Header>
                <Card.Content
                  ref={mergeRefs([contentRef, parentContentRef])}
                  className={clsx(classes.modalContent, customClasses?.content, {})}
                >
                  <div className={clsx(classes.contentWrapper, customClasses?.contentWrapper)}>
                    {children}
                  </div>
                </Card.Content>
                {footer && (
                  <ModalFooter className={classes.footer}>
                    {footer}
                  </ModalFooter>
                )}
              </Card>
            </DialogPanel>
          </Wrapper>
        </Dialog>
      </ModalContextProvider>
    );
  }
);